'use client'

import React, { useState, useEffect, useRef, useMemo } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import Image from 'next/image'
import styles from './Services.module.scss'
import { ComponentProps, RepeaterField } from '@/types/database'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeBasicHtml } from '@/lib/utils/html-sanitizer'
import { CustomPlayButton } from '@/components/CustomPlayButton'

interface ServiceItem extends RepeaterField {
  title?: string
  description?: string
}

interface GalleryImage extends RepeaterField {
  imageUrl?: string
  alt?: string
}

interface ServicesProps extends ComponentProps {
  title?: string
  mediaType?: 'image' | 'video' | 'gallery'
  imageUrl?: string
  vimeoId?: string
  videoStyle?: 'embedded' | 'background'
  aspectRatio?: string
  galleryImages?: GalleryImage[]
  gallerySpeed?: number
  // Legacy support
  mediaUrl?: string
  imageAlignment?: 'left' | 'right'
  services?: ServiceItem[]
  priority?: boolean
  noTopPadding?: boolean
  noBottomPadding?: boolean
}

// Custom hook for individual service item animation
const useServiceItemAnimation = () => {
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad

  useEffect(() => {
    const element = ref.current
    
    if (!element) return
    
    // In admin mode, always show immediately
    if (isAdminMode) {
      setShouldAnimate(true)
      return
    }
    
    // If already animated, don't re-animate
    if (hasAnimated) {
      setShouldAnimate(true)
      return
    }

    // Only set up observer if we can animate and haven't animated yet
    if (!canAnimate) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting) {
          setShouldAnimate(true)
          setHasAnimated(true)
          observer.disconnect() // Stop observing once animated
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [canAnimate, isAdminMode, hasAnimated])

  return { ref, shouldAnimate }
}

// Memoized service item component to prevent unnecessary re-renders
const ServiceItemComponent = React.memo<{
  service: ServiceItem
  index: number
  isAdminMode: boolean
}>(({ service, index, isAdminMode }) => {
  const { ref, shouldAnimate } = useServiceItemAnimation()

  return (
    <motion.div 
      ref={ref}
      className={styles['services__list--item']}
      initial={{ opacity: isAdminMode ? 1 : 0 }}
      animate={{ opacity: shouldAnimate ? 1 : 0 }}
      transition={{
        duration: 0.8,
        delay: 0.4 + (index * 0.2), // Staggered after title
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      {service.title && (
        <motion.h3 
          className={styles['services__list--item-title']}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: shouldAnimate ? 1 : 0 }}
          transition={{
            duration: 0.6,
            delay: 0.4 + (index * 0.2), // Same as parent
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(service.title) }}
        />
      )}
      
      {service.description && (
        <motion.p 
          className={styles['services__list--item-description']}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: shouldAnimate ? 1 : 0 }}
          transition={{
            duration: 0.6,
            delay: 0.4 + (index * 0.2) + 0.1, // Slightly after title
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(service.description) }}
        />
      )}
    </motion.div>
  )
})

ServiceItemComponent.displayName = 'ServiceItemComponent'

export const Services: React.FC<ServicesProps> = ({
  title,
  mediaType = 'image',
  imageUrl,
  vimeoId,
  videoStyle = 'embedded',
  aspectRatio = '16/9',
  galleryImages = [],
  gallerySpeed = 3000,
  // Legacy support
  mediaUrl,
  imageAlignment = 'left',
  services = [],
  priority = false,
  noTopPadding = false,
  noBottomPadding = false
}) => {
  const servicesArray = Array.isArray(services) ? services : []
  const galleryImagesArray = useMemo(() => 
    Array.isArray(galleryImages) ? galleryImages : [], 
    [galleryImages]
  )
  
  // Animation states
  const [mediaInView, setMediaInView] = useState(false)
  const [titleInView, setTitleInView] = useState(false)
  
  // Gallery states
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isGalleryLoaded, setIsGalleryLoaded] = useState(false)
  
  // Refs for intersection observers
  const mediaRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const galleryIntervalRef = useRef<NodeJS.Timeout | null>(null)
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  

  
  // Check if animations should be enabled
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad

  // Media intersection observer
  useEffect(() => {
    const element = mediaRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setMediaInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setMediaInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Title intersection observer
  useEffect(() => {
    const element = titleRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setTitleInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setTitleInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Gallery rotation effect
  useEffect(() => {
    if (mediaType !== 'gallery' || galleryImagesArray.length <= 1 || isAdminMode) {
      return
    }

    // Only start rotation after gallery is loaded and visible
    if (!isGalleryLoaded || !mediaInView) {
      return
    }

    const startGalleryRotation = () => {
      galleryIntervalRef.current = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          (prevIndex + 1) % galleryImagesArray.length
        )
      }, gallerySpeed)
    }

    startGalleryRotation()

    return () => {
      if (galleryIntervalRef.current) {
        clearInterval(galleryIntervalRef.current)
      }
    }
  }, [mediaType, galleryImagesArray.length, gallerySpeed, isGalleryLoaded, mediaInView, isAdminMode])

  // Preload gallery images
  useEffect(() => {
    if (mediaType !== 'gallery' || galleryImagesArray.length === 0) {
      return
    }

    let loadedCount = 0
    const totalImages = galleryImagesArray.length

    galleryImagesArray.forEach((image) => {
      if (image.imageUrl) {
        const img = new window.Image()
        img.onload = () => {
          loadedCount++
          
          // Mark gallery as loaded when all images are loaded
          if (loadedCount === totalImages) {
            setIsGalleryLoaded(true)
          }
        }
        img.onerror = () => {
          loadedCount++
          // Still count failed images toward completion
          if (loadedCount === totalImages) {
            setIsGalleryLoaded(true)
          }
        }
        img.src = image.imageUrl
      }
    })

    // Fallback: mark as loaded after timeout
    const timeoutId = setTimeout(() => {
      setIsGalleryLoaded(true)
    }, 10000) // 10 second timeout

    return () => clearTimeout(timeoutId)
  }, [mediaType, galleryImagesArray])

  const isVideo = (url: string) => {
    return url.match(/\.(mp4|webm|ogg|mov)$/i)
  }

  const isVimeoId = (value: string) => {
    // Check if it's just numbers (Vimeo ID) or contains vimeo.com
    return /^\d+$/.test(value.trim()) || value.includes('vimeo.com')
  }

  const getVimeoId = (value: string) => {
    if (/^\d+$/.test(value.trim())) {
      return value.trim()
    }
    // Extract ID from Vimeo URL
    const match = value.match(/vimeo\.com\/(\d+)/)
    return match ? match[1] : null
  }

  const renderMedia = () => {
    // Handle gallery media type
    if (mediaType === 'gallery') {
      if (galleryImagesArray.length === 0) return null
      
      return (
        <div className={styles['services__gallery']}>
          {galleryImagesArray.map((image, index) => (
            <Image
              key={image.id || index}
              src={image.imageUrl || '/gather-productions-share-image.jpg'}
              alt={image.alt || title || `Gallery image ${index + 1}`}
              className={`${styles['services__gallery-image']} ${
                index === currentImageIndex ? styles['services__gallery-image--active'] : ''
              }`}
              width={1200}
              height={800}
              quality={95}
              priority={index === 0 && priority}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 1200px"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                opacity: index === currentImageIndex ? 1 : 0,
                position: index === 0 ? 'relative' : 'absolute',
                top: index === 0 ? 'auto' : 0,
                left: index === 0 ? 'auto' : 0
              }}
            />
          ))}
        </div>
      )
    }

    // Determine the actual media URL and type for single image/video
    let actualMediaUrl: string | null = null
    let actualMediaType: 'image' | 'video' = 'image'
    
    // Use new props if available
    if (mediaType === 'video' && vimeoId) {
      actualMediaUrl = vimeoId.trim()
      actualMediaType = 'video'
    } else if (mediaType === 'image' && imageUrl) {
      actualMediaUrl = imageUrl
      actualMediaType = 'image'
    }
    // Fall back to legacy mediaUrl prop
    else if (mediaUrl) {
      actualMediaUrl = mediaUrl
      actualMediaType = isVimeoId(mediaUrl) ? 'video' : 'image'
    }
    
    if (!actualMediaUrl) return null
    
    if (actualMediaType === 'video' || isVimeoId(actualMediaUrl)) {
      // Determine video parameters based on style
      const vimeoParams = (mediaType === 'video' && videoStyle === 'background') || (mediaUrl && videoStyle === 'background')
        ? 'background=1&autoplay=1&loop=1&byline=0&title=0&portrait=0&muted=1&dnt=1'
        : 'badge=0&autopause=0&player_id=0&app_id=58479&byline=0&title=0&portrait=0&color=ffffff&controls=0&muted=1&dnt=1'
      
      return (
        <div className={styles['services__vimeo-wrapper']}>
          <iframe 
            src={`https://player.vimeo.com/video/${getVimeoId(actualMediaUrl)}?${vimeoParams}`}
            className={styles['services__vimeo']}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={title || 'Vimeo video'}
          />
          {/* Only show custom play button for embedded videos, not background videos */}
          {!((mediaType === 'video' && videoStyle === 'background') || (mediaUrl && videoStyle === 'background')) && (
            <CustomPlayButton 
              vimeoId={getVimeoId(actualMediaUrl)!}
              className={styles['services__custom-play-button']}
            />
          )}
        </div>
      )
    } else if (isVideo(actualMediaUrl)) {
      return (
        <video 
          src={actualMediaUrl} 
          className={styles['services__video']}
          controls
          playsInline
        />
      )
    } else {
      return (
        <Image 
          src={actualMediaUrl} 
          alt={title || 'Services'}
          className={styles['services__image']}
          width={1200}
          height={800}
          quality={95}
          priority={priority}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 1200px"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )
    }
  }

  const hasMedia = (mediaType === 'video' && vimeoId) || (mediaType === 'image' && imageUrl) || (mediaType === 'gallery' && galleryImagesArray.length > 0) || mediaUrl
  const isVideoContent = (mediaType === 'video' && vimeoId) || (mediaUrl && isVimeoId(mediaUrl))
  const isBackgroundVideo = isVideoContent && ((mediaType === 'video' && videoStyle === 'background') || (mediaUrl && videoStyle === 'background'))
  const isGalleryContent = mediaType === 'gallery' && galleryImagesArray.length > 0
  
  return (
    <section className={styles['services']}>
      <div className={`${styles['services__container']} ${imageAlignment === 'right' ? styles['services__container--reverse'] : ''} ${
        noTopPadding && noBottomPadding ? styles['services__container--no-vertical'] :
        noTopPadding ? styles['services__container--no-top'] :
        noBottomPadding ? styles['services__container--no-bottom'] : ''
      }`}>
        {hasMedia && (
                    <motion.div 
            ref={mediaRef}
            className={`${styles['services__media-wrapper']} ${isVideoContent ? styles['services__media-wrapper--video'] : ''} ${isBackgroundVideo ? styles['services__media-wrapper--background'] : ''} ${isGalleryContent ? styles['services__media-wrapper--gallery'] : ''}`}
            style={isVideoContent ? { aspectRatio: aspectRatio } : {}}
            initial={{ opacity: isAdminMode ? 1 : 0 }}
            animate={{ opacity: mediaInView ? 1 : 0 }}
            transition={{
              duration: 0.8,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          >
            {renderMedia()}
          </motion.div>
        )}
        
        <div className={styles['services__content']}>
          {title && (
                        <motion.h2 
              ref={titleRef}
              className={styles['services__title']}
              initial={{ opacity: isAdminMode ? 1 : 0 }}
              animate={{ opacity: titleInView ? 1 : 0 }}
              transition={{
                duration: 0.8,
                delay: 0.2, // Appears after media
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(title) }}
            />
          )}
          
          {servicesArray.length > 0 && (
            <div className={styles['services__list']}>
              {servicesArray.map((service, index) => (
                <ServiceItemComponent
                  key={service.id}
                  service={service}
                  index={index}
                  isAdminMode={isAdminMode}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
} 